
import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { cs_CZ, en_US, NzI18nInterface, NzI18nService } from 'ng-zorro-antd/i18n';
import { supportedUiLanguages } from '../models/language.model';
import { LanguageStateService } from './language-state.service';

/**
 * Jediné místo, které přepíná jazyk aplikace.
 *
 * Přepnout je potřeba vždy obojí — ngx-translate (texty aplikace) i ng-zorro locale
 * (datepicker, tabulky, prázdné stavy). Dřív to dělal každý layout po svém a start
 * aplikace jazyk ignoroval úplně.
 *
 * Třetí věc je `<html lang>` — podle něj čtečka obrazovky volí výslovnost a prohlížeč
 * dělení slov. Zůstával `cs` i v anglickém rozhraní, takže čtečka četla angličtinu
 * českou výslovností.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {

  private readonly nzLocales: { [languageIso: string]: NzI18nInterface } = {
    cs: cs_CZ,
    en: en_US,
  };

  /** Jazyky, které {@link preload} právě stahuje — opakované otevření dialogu je nestáhne znovu. */
  private readonly preloading = new Set<string>();

  constructor(
    private translate: TranslateService,
    private nzI18nService: NzI18nService,
    private languageState: LanguageStateService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  get supported(): string[] {
    return supportedUiLanguages;
  }

  /** Aktuální jazyk. Zdrojem je LanguageStateService, odkud ho čte i HTTP interceptor. */
  get current(): string {
    return this.languageState.current;
  }

  /**
   * Nastaví jazyk odvozený odjinud — z odkazu v e-mailu nebo z prohlížeče.
   * Nepodporovaná nebo prázdná hodnota spadne na výchozí.
   */
  use(languageIso: string | undefined | null): void {
    this.apply(this.languageState.set(languageIso));
  }

  /**
   * Jazyk z účtu. Je to jen výchozí hodnota pro zařízení, kde si uživatel ještě nevybral —
   * `User.LanguageIso` řídí jazyk e-mailů, ne rozhraní.
   *
   * Zrcadlí `ThemeService.applyFromAccount()`, ale obráceně: u motivu vyhrává účet,
   * u jazyka zařízení. Guard je schválně tady, ne u volajícího — `setCurrentUser` se volá
   * z šesti míst (start aplikace, login, přepnutí firmy, uložení jména, notifikací, motivu)
   * a bez něj by ruční volbu shodilo i obyčejné uložení jména v profilu.
   */
  useFromAccount(languageIso: string | undefined | null): void {
    if (this.languageState.chosen) {
      return;
    }

    this.use(languageIso);
  }

  /** Ruční volba uživatele — vlaječkou nebo v profilu. Zůstává v tomhle prohlížeči. */
  choose(languageIso: string): void {
    this.apply(this.languageState.choose(languageIso));
  }

  /**
   * Text v konkrétním jazyce bez ohledu na jazyk rozhraní.
   *
   * Náhled e-mailu musí být celý v jazyce, ve kterém se e-mail píše — český admin
   * skládající anglickou pozvánku potřebuje vidět anglickou verzi, ne svoji.
   * Vyžaduje načtené překlady daného jazyka, viz {@link preload}; jinak spadne
   * na jazyk rozhraní, což je pořád lepší než holý klíč.
   */
  textIn(languageIso: string, key: string, params?: Record<string, unknown>): string {
    const translations = this.translate.translations[languageIso];

    return translations
      ? this.translate.getParsedResult(translations, key, params)
      : this.translate.instant(key, params);
  }

  /**
   * Načte překlady jazyka do paměti, aby je {@link textIn} měla z čeho brát.
   *
   * Schválně jde přímo přes loader, ne přes `translate.getTranslation()`. To v ngx-translate 14
   * nastaví `pending` a `get()` — tedy i `| translate` — pak do doby, než soubor dorazí,
   * překládá z načítaného jazyka místo z jazyka rozhraní. Dialog e-mailové šablony, který
   * volá `preload` při otevření, se proto napoprvé vykreslil anglicky a pipe si to podržel.
   */
  preload(languageIso: string): void {
    if (this.translate.translations[languageIso] || this.preloading.has(languageIso)) {
      return;
    }

    this.preloading.add(languageIso);
    this.translate.currentLoader.getTranslation(languageIso)
      .pipe(finalize(() => this.preloading.delete(languageIso)))
      .subscribe({
        next: (translations) => this.translate.setTranslation(languageIso, translations, true),
        // Bez překladů textIn spadne na jazyk rozhraní; další otevření dialogu to zkusí znovu.
        error: () => undefined,
      });
  }

  private apply(language: string): void {
    this.translate.use(language);
    this.nzI18nService.setLocale(this.nzLocales[language]);
    this.document.documentElement.lang = language;
  }
}
